import type { AudioFilterConfig } from "@/types/metrics/metadata"

import type { MetricDirectoryEntry } from "../hooks/useMetricDirectory"

// `requires_single_speaker` and `requires_audio_filter` arrive with the next published
// directory contract. They are read by key until `src/types_api/backend/` carries them.

/** A supplied audio filter must name exactly one speaker. */
export function requiresSingleSpeaker(
  entry: MetricDirectoryEntry | undefined,
): boolean {
  return (
    entry !== undefined &&
    "requires_single_speaker" in entry &&
    entry.requires_single_speaker === true
  )
}

/** The metric cannot run without an audio filter naming one speaker. */
export function requiresAudioFilter(
  entry: MetricDirectoryEntry | undefined,
): boolean {
  return (
    entry !== undefined &&
    "requires_audio_filter" in entry &&
    entry.requires_audio_filter === true
  )
}

/**
 * The speaker scope the backend write path fills for a metric that cannot run
 * unscoped: its declared default role. Undefined when the metric runs without a
 * filter or declares no agent or persona default.
 */
export function directoryDefaultAudioFilter(
  entry: MetricDirectoryEntry | undefined,
): AudioFilterConfig | undefined {
  if (entry === undefined || !requiresAudioFilter(entry)) {
    return undefined
  }
  const role = entry.default_evaluated_role
  if (role !== "agent" && role !== "persona") {
    return undefined
  }
  return { include: [{ type: "speaker", speakers: [role] }] }
}
